<template>
  <slot name="header_close" />
  <div class="wrapper_dialog">
    <h3 class="title">
      {{ !!tagName ? $t("tags.edit_tag_number") : $t("tags.add_tag_number") }}
      <div class="number-block">
        <img class="icon" :src="messengerIcon" />
        <span>{{ number }}</span>
      </div>
    </h3>

    <form class="content" @submit.prevent="actionTag">
      <div class="field">
        <p class="subtitle">{{ $t("tags.name_label") }}</p>
        <input
          v-model="inputTag"
          type="text"
          :class="{ input_error: !!error }"
          :placeholder="$t('tags.name_placeholder')"
        />
        <div class="input-errors" :class="{ 'is-shown': !!error }">{{ error }}</div>
      </div>

      <div class="action_container">
        <ButtonLoading
          type="submit"
          :loading="button.loading"
          :disabled="button.disabled || !isSubmittable"
        >
          {{ !!tagName ? $t("common.save") : $t("common.add") }}
        </ButtonLoading>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import ButtonLoading from "@/components/Recurring/ButtonLoading.vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";

const store = useStore();
const { t: $t } = useI18n();

const button = ref({
  loading: false,
  disabled: false,
});

const inputTag = ref("");

const TAG_MAX_LENGTH = 20;

const number = computed(() => store.state.dialogParams?.info?.number);
const messenger = computed(() => store.state.dialogParams?.info?.messenger);
const messengerIcon = computed(() =>
  messenger.value ? require(`@/assets/icons/${messenger.value}.svg`).default : ""
);

const error = computed(() => {
  const value = inputTag.value || "";
  if (!value.trim()) return "";
  if (value.length > TAG_MAX_LENGTH)
    return $t("validation.max", { length: TAG_MAX_LENGTH });
  if (!/^[a-zA-Zа-яА-ЯёЁіІїЇєЄґҐ0-9]*$/.test(value))
    return $t("validation.alphanumeric");
  return "";
});

const isSubmittable = computed(
  () => !!inputTag.value?.trim() && !error.value && inputTag.value !== tagName.value
);

const tagName = computed(() => {
  inputTag.value = store.state.dialogParams?.info?.tags?.[0]?.name;
  return store.state.dialogParams?.info?.tags?.[0]?.name;
});

const tagId = computed(() => store.state.dialogParams?.info?.tags?.[0]?.id || null);

const dialogParams = computed(() => store.state.dialogParams);

const onToggleLoadingButton = () => {
  if (button.value.loading) {
    button.value.loading = false;
    button.value.disabled = false;
  } else {
    button.value.loading = true;
    button.value.disabled = true;
  }
};

const actionTag = async () => {
  if (!isSubmittable.value) return;

  const {
    info: { number, messenger, visibility },
  } = dialogParams.value;

  const payload = {
    name: inputTag.value,
    number,
    messenger,
  };

  onToggleLoadingButton();

  if (!!tagId.value) {
    payload.id = tagId.value;
    store
      .dispatch("numbers/editTag", payload)
      // TODO: refactoring repeat trycatch
      .then((res) => {
        if (!!store.getters.getNextDialog) {
          const updateTag = {
            name: res.data.name,
            id: tagId.value,
          };
          store.commit("setTagsToDialogParams", [updateTag]);
          store.commit("setDialog", store.getters.getNextDialog);
          store.commit("setNextDialog", "");
        } else {
          store.commit("closeDialog");
        }
        store.dispatch("numbers/loadNumbersByMessenger", {
          messenger,
          visibility,
        });
        // store.dispatch("numbers/loadConnectedNumbers");
      })
      .catch(() => {
        onToggleLoadingButton();
      });
  } else {
    store
      .dispatch("numbers/addTag", payload)
      .then((res) => {
        if (!!store.getters.getNextDialog) {
          store.commit("setTagsToDialogParams", [res.data]);
          store.commit("setDialog", store.getters.getNextDialog);
          store.commit("setNextDialog", "");
        } else {
          store.commit("closeDialog");
        }
        store.dispatch("numbers/loadNumbersByMessenger", {
          messenger,
          visibility,
        });
        // store.dispatch("numbers/loadConnectedNumbers");
      })
      .catch(() => {
        onToggleLoadingButton();
      });
  }
};
</script>

<style lang="scss" scoped>
@use "@/styles/dialog-form.scss";
</style>
